import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  FileText, Presentation, FileType2, Sheet, FileSpreadsheet, Image as ImageIcon,
  Braces, Code2, Globe, Package, Loader2, Download, CalendarClock, Sparkles, Save,
  ChevronUp, ChevronDown, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import SchedulePicker, { defaultScheduleSpec } from '@/components/SchedulePicker';
import { getBrandName, getBranding } from '@/lib/branding';

// Fetch an image URL and return a data URI so server-side exporters can embed
// the logo bytes directly (works for PDF/DOCX/PPTX/XLSX/HTML). Never throws —
// a logo failure must not block the export.
async function logoDataUri(preferUrl) {
  const b = getBranding() || {};
  const isDark = document.documentElement.classList.contains('dark');
  const url = preferUrl || b.logo_url || (isDark ? '/inzara-dark.png' : '/inzara-light.png');
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/') || blob.size > 2_000_000) return null;
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}


// Format catalog — order mirrors the product spec/mockup. `soon` = priority-2,
// shown but disabled. `png` is captured client-side; the rest go server-side.
const FORMATS = [
  { key: 'pdf', label: 'PDF', sub: 'Report', icon: FileText, ext: 'pdf', color: 'text-red-500' },
  { key: 'pptx', label: 'PowerPoint', sub: 'Presentation', icon: Presentation, ext: 'pptx', color: 'text-orange-500' },
  { key: 'docx', label: 'Word', sub: 'Document', icon: FileType2, ext: 'docx', color: 'text-blue-500' },
  { key: 'xlsx', label: 'Excel', sub: 'Data', icon: Sheet, ext: 'xlsx', color: 'text-emerald-500' },
  { key: 'csv', label: 'CSV', sub: 'Data', icon: FileSpreadsheet, ext: 'csv', color: 'text-teal-500' },
  { key: 'png', label: 'PNG', sub: 'Image', icon: ImageIcon, ext: 'png', color: 'text-fuchsia-500' },
  { key: 'json', label: 'JSON', sub: 'Structured', icon: Braces, ext: 'json', color: 'text-amber-500' },
  { key: 'xml', label: 'XML', sub: 'Structured', icon: Code2, ext: 'xml', color: 'text-amber-500' },
  { key: 'html', label: 'HTML', sub: 'Web', icon: Globe, ext: 'html', color: 'text-sky-500' },
  { key: 'package', label: 'Package', sub: 'All Files', icon: Package, ext: 'zip', color: 'text-indigo-500' },
];

const IMAGE_FORMATS = ['pdf', 'docx', 'pptx', 'png', 'html', 'package'];
const DATA_FORMATS = ['xlsx', 'csv', 'json', 'xml'];
const COMPOSE_FORMATS = ['pdf', 'docx', 'pptx', 'html'];

// Composite the brand logo as a header band on top of a captured PNG dataURL.
// Falls back to the raw capture if no logo is available.
function _loadImg(src) {
  return new Promise((resolve, reject) => {
    const im = new Image(); im.onload = () => resolve(im); im.onerror = reject; im.src = src;
  });
}
async function pngWithLogo(imgDataUrl) {
  try {
    const logo = await logoDataUri();
    const base = await _loadImg(imgDataUrl);
    if (!logo) return await (await fetch(imgDataUrl)).blob();
    const lg = await _loadImg(logo);
    const logoH = Math.max(56, Math.round(base.height * 0.05));
    const logoW = Math.round(logoH * (lg.width / lg.height || 3));
    const pad = Math.round(logoH * 0.4);
    const headerH = logoH + pad * 2;
    const canvas = document.createElement('canvas');
    canvas.width = base.width; canvas.height = base.height + headerH;
    const cx = canvas.getContext('2d');
    cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, canvas.width, canvas.height);
    cx.drawImage(lg, pad, pad, logoW, logoH);
    cx.drawImage(base, 0, headerH);
    return await new Promise(res => canvas.toBlob(res, 'image/png'));
  } catch { return await (await fetch(imgDataUrl)).blob(); }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function captureSelector(sel) {
  if (!sel) return null;
  const el = document.querySelector(sel);
  if (!el) return null;
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
    return canvas.toDataURL('image/png');
  } catch { return null; }
}

const ChecklistRow = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-xs cursor-pointer py-0.5" data-testid={testid}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-primary w-3.5 h-3.5" />
    <span className="text-foreground/90">{label}</span>
  </label>
);

export default function ExportFrameworkDialog({
  open, onOpenChange, mode = 'report',
  resourceId, resourceName, resourceType = 'dashboard', getContext,
  nativeReportPdf = false, onNativePdf,
  exporting = false, exportProgress = null, onCancelExport,
}) {
  const isAi = mode === 'ai';
  const [ctx, setCtx] = useState(null);
  const [format, setFormat] = useState('pdf');
  const [tab, setTab] = useState('content');
  const [busy, setBusy] = useState(false);
  const [pixelPerfect, setPixelPerfect] = useState(false);
  const abortRef = useRef(null);
  // Busy = this dialog's own server export OR a parent-driven native PDF render.
  const isBusy = busy || exporting;

  // Content / scope
  const [scope, setScope] = useState(isAi ? 'complete' : 'entire_report');
  const [pageIds, setPageIds] = useState([]);
  const [filterMode, setFilterMode] = useState('current'); // current | default | none | choose
  // AI include checklist
  const [include, setInclude] = useState({
    question: true, summary: true, findings: true, root_causes: true, recommendations: true,
    visuals: true, underlying_data: true, definitions: true, methodology: true, lineage: true,
    filters: true, as_of: true, conversation: false,
  });
  // Data options
  const [valuesMode, setValuesMode] = useState('formatted'); // formatted | raw
  const [rowLimit, setRowLimit] = useState(10000);
  // Governance
  const [gov, setGov] = useState({
    include_definitions: true, include_ids: true, include_versions: true,
    include_filter_context: true, include_lineage: true, include_disclosure: true,
  });
  // Appearance
  const [branding, setBranding] = useState('customer');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [filename, setFilename] = useState('');
  // Delivery
  const [delivery, setDelivery] = useState('download'); // download | schedule
  const [recipients, setRecipients] = useState('');
  const [spec, setSpec] = useState(defaultScheduleSpec());
  // AI Composed Docs + Presets
  const [composeAi, setComposeAi] = useState(false);
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);

  const kindForPreset = isAi ? 'ai' : mode;

  const loadPresets = async () => {
    try { const r = await api.listExportPresets(kindForPreset); setPresets(r.presets || []); } catch { /* noop */ }
  };

  useEffect(() => {
    if (!open) return;
    setTab('content');
    setDelivery('download');
    setComposeAi(false);
    loadPresets();
    (async () => {
      try {
        const c = typeof getContext === 'function' ? await getContext() : getContext;
        setCtx(c || {});
        setTitle(c?.title || resourceName || 'Export');
        setFilename(c?.defaultFilename || c?.title || resourceName || 'export');
        setPageIds((c?.pages || []).map(p => p.id));
        setScope(isAi ? 'complete' : (mode === 'dashboard' ? 'entire_dashboard' : 'entire_report'));
      } catch (e) { toast.error(e.message || 'Could not load export context'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentConfig = () => ({
    format, scope, filter_mode: filterMode, values: valuesMode, row_limit: rowLimit,
    governance: gov, branding, ai_include: isAi ? include : undefined, compose_ai: composeAi,
  });

  const applyPreset = (p) => {
    const c = p.config || {};
    if (c.format && FORMATS.find(f => f.key === c.format && !f.soon)) setFormat(c.format);
    if (c.scope) setScope(c.scope);
    if (c.filter_mode) setFilterMode(c.filter_mode);
    if (c.values) setValuesMode(c.values);
    if (c.row_limit) setRowLimit(c.row_limit);
    if (c.governance) setGov(g => ({ ...g, ...c.governance }));
    if (c.branding) setBranding(c.branding);
    if (isAi && c.ai_include) setInclude(i => ({ ...i, ...c.ai_include }));
    setComposeAi(!!c.compose_ai);
    toast.success(`Applied preset "${p.name}"`);
  };

  const saveAsPreset = async () => {
    const name = window.prompt('Name this export preset (e.g. "Board Package"):', title);
    if (!name) return;
    setSavingPreset(true);
    try {
      await api.createExportPreset({ name, kind: kindForPreset, config: currentConfig(), shared: true });
      toast.success('Preset saved');
      loadPresets();
    } catch (e) { toast.error(e.message || 'Could not save preset'); }
    finally { setSavingPreset(false); }
  };

  const removePreset = async (id) => {
    try { await api.deleteExportPreset(id); loadPresets(); } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const tabs = isAi
    ? [['content', 'Content'], ['data', 'Data'], ['appearance', 'Appearance'], ['ai', 'AI² Options'], ['governance', 'Governance'], ['delivery', 'Delivery']]
    : [['content', 'Content'], ['data', 'Data'], ['appearance', 'Layout & Appearance'], ['options', 'Options'], ['governance', 'Governance'], ['delivery', 'Delivery']];

  const scopeCards = isAi
    ? [['complete', 'Complete Analysis', 'Question, findings, visuals, data'], ['visuals', 'Visuals Only', 'Charts and tables'],
       ['data', 'Data Only', 'Underlying data & calcs'], ['findings', 'Findings Only', 'Key findings & recommendations']]
    : [['current_visual', 'Current Visual', 'Selected chart, table or KPI'], ['selected_visuals', 'Selected Visuals', 'Multiple selected items'],
       ['current_page', 'Current Page', 'Everything on this page'], ['entire_report', 'Entire Report', 'All pages / tabs'],
       ['entire_dashboard', 'Entire Dashboard', 'Full dashboard (all pages)']];

  // Resolve the visuals in scope from the loaded context.
  const scopedVisuals = useMemo(() => {
    const all = ctx?.visuals || [];
    if (isAi) {
      if (scope === 'data' || scope === 'findings') return all; // handled via flags; data still needed
      return all;
    }
    if (scope === 'current_visual') return all.filter(v => v.id === ctx?.currentVisualId);
    if (scope === 'selected_visuals') return all.filter(v => (ctx?.selectedVisualIds || []).includes(v.id));
    if (scope === 'current_page') return all.filter(v => !v.pageId || v.pageId === ctx?.currentPageId);
    return all;
  }, [ctx, scope, isAi]);

  const activeFilters = useMemo(() => {
    if (filterMode === 'none') return [];
    return ctx?.filters || [];
  }, [ctx, filterMode]);

  // Column picker — applies to the primary (first non-empty) table visual.
  const primaryVisual = useMemo(() => scopedVisuals.find(v => (v.columns || []).length) || null, [scopedVisuals]);
  const [colOrder, setColOrder] = useState([]);   // ordered full column list
  const [colHidden, setColHidden] = useState({}); // { key: true } => excluded
  useEffect(() => {
    setColOrder(primaryVisual?.columns || []);
    setColHidden({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryVisual?.id, open]);
  const moveCol = (i, dir) => setColOrder(prev => {
    const arr = [...prev]; const j = i + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const previewCols = (colOrder.length ? colOrder.filter(c => !colHidden[c]) : (primaryVisual?.columns || []));
  const includeData = DATA_FORMATS.includes(format) || (isAi ? include.underlying_data : true);

  const buildPayload = async (composed) => {
    const wantImages = IMAGE_FORMATS.includes(format) && !(isAi && scope === 'data');
    const visuals = [];
    for (const v of scopedVisuals) {
      const includeData = DATA_FORMATS.includes(format) || (isAi ? include.underlying_data : true);
      let image = null;
      if (wantImages && (isAi ? include.visuals : true)) image = await captureSelector(v.selector);
      // Honor the column picker for the primary table visual.
      let cols = v.columns || [];
      if (!isAi && primaryVisual && v.id === primaryVisual.id && colOrder.length) {
        cols = colOrder.filter(c => !colHidden[c]);
      }
      const labels = {};
      cols.forEach(c => { labels[c] = (v.column_labels || {})[c] || c; });
      visuals.push({
        id: v.id, title: v.title, type: v.type,
        columns: includeData ? cols : [],
        column_labels: includeData ? labels : {},
        rows: includeData ? (v.rows || []).slice(0, rowLimit) : [],
        total_rows: v.total_rows ?? (v.rows || []).length,
        // Exact on-screen grand-total + per-group subtotal rows (avg/ratio-aware),
        // composed by the report view, so PPTX/Excel totals match the screen.
        totals: includeData ? (v.totals || null) : null,
        subtotals: includeData ? (v.subtotals || []) : [],
        image,
      });
    }
    const baseNarr = isAi ? {
      question: ctx?.narrative?.question, summary: ctx?.narrative?.summary,
      findings: ctx?.narrative?.findings || [], recommendations: ctx?.narrative?.recommendations || [],
      root_causes: ctx?.narrative?.root_causes || [], methodology: ctx?.narrative?.methodology,
      include_question: include.question, include_summary: include.summary,
      include_findings: include.findings, include_recommendations: include.recommendations,
      include_root_causes: include.root_causes, include_methodology: include.methodology,
    } : {};
    // AI-composed Executive brief overrides/fills the narrative sections.
    const narrative = composed ? {
      ...baseNarr,
      summary: composed.summary || baseNarr.summary,
      findings: composed.findings?.length ? composed.findings : baseNarr.findings,
      recommendations: composed.recommendations?.length ? composed.recommendations : baseNarr.recommendations,
      methodology: composed.methodology || baseNarr.methodology,
      include_summary: true, include_findings: true, include_recommendations: true, include_methodology: true,
    } : baseNarr;
    return {
      format, kind: isAi ? 'ai_analysis' : mode, title, subtitle,
      scope: (scopeCards.find(s => s[0] === scope) || [])[1] || scope,
      filename, as_of: (isAi ? include.as_of : true) ? (ctx?.asOf || null) : null,
      branding, brand_name: getBrandName(), logo_data_uri: await logoDataUri(ctx?.themeLogoUrl),
      theme_color: ctx?.themeColor || null,
      filters: (isAi ? include.filters : true) ? activeFilters : [],
      data_options: { values: valuesMode, row_limit: rowLimit },
      governance: {
        ...gov,
        certified: (isAi ? include.definitions : gov.include_definitions) ? (ctx?.governance?.certified || []) : [],
        lineage: (isAi ? include.lineage : gov.include_lineage) ? (ctx?.governance?.lineage || []) : [],
        disclosure: ctx?.governance?.disclosure || '',
      },
      narrative,
      visuals,
    };
  };

  const doExport = async () => {
    if (delivery === 'schedule') return doSchedule();
    if (delivery === 'email') return doEmail();
    // Report-layout PDF → crisp multi-page headless-Chromium server render that
    // matches the on-screen paginated report, carrying the branded footer and
    // keeping the browser responsive (progress + cancel below). Dashboards use
    // the fast structured server export (reportlab) by default, OR the pixel-
    // perfect Chromium render of the on-screen grid when the toggle is on.
    const usePixel = pixelPerfect && !nativeReportPdf;
    if (format === 'pdf' && typeof onNativePdf === 'function' && (nativeReportPdf || usePixel)) {
      setBusy(true);
      try { await onNativePdf(Array.isArray(pageIds) && pageIds.length ? pageIds : null, { pixel: usePixel, filename }); onOpenChange(false); }
      catch (e) { if (e && e.cancelled) toast.info('Export cancelled'); else toast.error(e.message || 'Export failed'); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      if (format === 'png') {
        const target = scopedVisuals[0]?.selector || ctx?.pageSelector || '[data-testid="visual-canvas"]';
        const img = await captureSelector(target);
        if (!img) throw new Error('Nothing to capture for PNG');
        const blob = await pngWithLogo(img);
        downloadBlob(blob, `${(filename || 'export').replace(/[^\w.-]/g, '_')}.png`);
        toast.success('Exported PNG');
        onOpenChange(false);
        return;
      }
      let composed = null;
      if (composeAi && COMPOSE_FORMATS.includes(format)) {
        try {
          composed = await api.composeExport({
            title, question: ctx?.narrative?.question || title,
            narrative: ctx?.narrative || {},
            visuals: scopedVisuals.map(v => ({ title: v.title, columns: v.columns, rows: (v.rows || []).slice(0, 20) })),
          });
          toast.success('AI² composed the brief');
        } catch { toast.error('AI composition unavailable — exporting a plain snapshot'); }
      }
      const payload = await buildPayload(composed);
      abortRef.current = new AbortController();
      const blob = await api.generateExport(payload, abortRef.current.signal);
      const ext = (FORMATS.find(f => f.key === format) || {}).ext || format;
      const realExt = (format === 'csv' && (payload.visuals.filter(v => v.rows.length).length > 1)) ? 'zip' : ext;
      downloadBlob(blob, `${(filename || 'export').replace(/[^\w.-]/g, '_')}.${realExt}`);
      toast.success(`Exported ${format.toUpperCase()}`);
      onOpenChange(false);
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.cancelled)) toast.info('Export cancelled');
      else toast.error(e.message || 'Export failed');
    }
    finally { setBusy(false); }
  };

  const cancelExport = () => {
    try { abortRef.current?.abort(); } catch { /* no-op */ }
    if (typeof onCancelExport === 'function') onCancelExport();
  };

  const doEmail = async () => {
    const list = recipients.split(/[,\n;]/).map(s => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient email'); return; }
    setBusy(true);
    const tId = toast.loading('Generating & emailing export…');
    try {
      const payload = await buildPayload(null);
      payload.recipients = list;
      const res = await api.emailExport(payload);
      toast.success(`Emailed to ${res.sent} recipient${res.sent === 1 ? '' : 's'}`, { id: tId });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Could not email export', { id: tId });
    } finally { setBusy(false); }
  };

  const doSchedule = async () => {
    const list = recipients.split(/[,\n;]/).map(s => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient email'); return; }
    setBusy(true);
    try {
      const export_config = { ...currentConfig(), title, subtitle, theme_color: ctx?.themeColor || null };
      const created = await api.createExportSchedule({
        dashboard_id: resourceId, dashboard_name: resourceName || title, resource_type: resourceType,
        resources: [{ id: resourceId, name: resourceName || title, type: resourceType }],
        recipients: list, schedule: spec, timezone: spec.timezone, export_config,
      });
      if (spec.run_now && created?.schedule_id) {
        try { await api.sendTestExportSchedule(created.schedule_id); toast.success('Export scheduled & sent'); }
        catch (e) { toast.warning(`Scheduled, but the test send failed: ${e.message || 'email not available'}`); }
      } else {
        toast.success('Export scheduled');
      }
      onOpenChange(false);
    } catch (e) { toast.error(e.message || 'Could not schedule'); }
    finally { setBusy(false); }
  };

  const fmt = FORMATS.find(f => f.key === format) || FORMATS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0 gap-0 glass-card border-border/50" data-testid="export-framework-dialog"
        onInteractOutside={(e) => { if (isBusy) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isBusy) e.preventDefault(); }}>
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border/40">
          <DialogTitle className="flex items-center gap-2 font-heading">
            {isAi ? <Sparkles className="w-5 h-5 text-primary" /> : <Download className="w-5 h-5 text-primary" />}
            {isAi ? 'Export AI² Analysis' : 'Export Report / Dashboard'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isAi ? 'Export your analysis, including findings, visuals, data and recommendations.'
                  : 'Export your report or dashboard, including visuals, data and layout.'}
          </p>
        </DialogHeader>

        {/* Format cards */}
        <div className="px-6 pt-4">
          <div className="grid grid-cols-5 gap-2">
            {FORMATS.map(f => {
              const Icon = f.icon;
              const active = format === f.key;
              return (
                <button key={f.key} disabled={f.soon}
                  onClick={() => setFormat(f.key)}
                  data-testid={`export-format-${f.key}`}
                  className={`relative p-2 rounded-xl border text-left transition-all active:scale-95 ${
                    f.soon ? 'opacity-40 cursor-not-allowed border-border/20'
                    : active ? 'border-primary/50 bg-primary/10 ring-1 ring-primary/20' : 'border-border/30 hover:border-border/60 hover:bg-muted/20'}`}>
                  <Icon className={`w-4 h-4 mb-1 ${f.soon ? 'text-muted-foreground/50' : f.color}`} strokeWidth={2.2} />
                  <p className="text-[11px] font-medium text-foreground leading-tight">{f.label}</p>
                  <p className="text-[8px] text-muted-foreground/80">{f.sub}</p>
                  {f.soon && <span className="absolute top-1 right-1 text-[7px] px-1 rounded bg-muted/60 text-muted-foreground">Soon</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Presets — one-click Quick Export */}
        <div className="px-6 pt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Presets</span>
          {presets.length === 0 && <span className="text-[10px] text-muted-foreground/50">None saved yet</span>}
          {presets.map(p => (
            <span key={p.preset_id} className="group inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border border-border/40 bg-muted/30 text-[10px] hover:border-primary/50">
              <button onClick={() => applyPreset(p)} data-testid={`preset-apply-${p.preset_id}`} className="text-foreground/90 hover:text-primary">{p.name}</button>
              <button onClick={() => removePreset(p.preset_id)} data-testid={`preset-del-${p.preset_id}`} className="text-muted-foreground/40 hover:text-red-400 px-0.5" title="Delete preset">×</button>
            </span>
          ))}
          <button onClick={saveAsPreset} disabled={savingPreset} data-testid="preset-save-btn"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-border/50 text-[10px] text-muted-foreground hover:text-primary hover:border-primary/50">
            <Save className="w-2.5 h-2.5" /> Save as preset
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-border/40 overflow-x-auto">
          {tabs.map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`export-tab-${key}`}
              className={`px-2.5 pb-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {lbl}
            </button>
          ))}
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          {tab === 'content' && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">What would you like to export?</p>
                <div className={`grid ${isAi ? 'grid-cols-4' : 'grid-cols-5'} gap-2`}>
                  {scopeCards.map(([key, lbl, desc]) => (
                    <button key={key} onClick={() => setScope(key)} data-testid={`export-scope-${key}`}
                      className={`p-2.5 rounded-xl border text-center transition-all active:scale-95 ${
                        scope === key ? 'border-primary/50 bg-primary/10' : 'border-border/30 hover:border-border/60'}`}>
                      <p className="text-[11px] font-medium text-foreground">{lbl}</p>
                      <p className="text-[8px] text-muted-foreground/80 mt-0.5">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {!isAi && (ctx?.pages || []).length > 1 && (scope === 'entire_report' || scope === 'entire_dashboard') && (
                <div>
                  <Label className="text-xs text-muted-foreground">Pages to include</Label>
                  <div className="mt-1 space-y-1 rounded-lg border border-border/40 p-2 max-h-32 overflow-auto">
                    {(ctx.pages).map(p => (
                      <ChecklistRow key={p.id} label={p.name} testid={`export-page-${p.id}`}
                        checked={pageIds.includes(p.id)}
                        onChange={c => setPageIds(prev => c ? [...prev, p.id] : prev.filter(x => x !== p.id))} />
                    ))}
                  </div>
                </div>
              )}

              {isAi && (
                <div>
                  <p className="text-sm font-medium mb-1">Include in export</p>
                  <div className="grid grid-cols-2 gap-x-4">
                    <ChecklistRow label="Original question and context" checked={include.question} onChange={v => setInclude(p => ({ ...p, question: v }))} testid="ai-inc-question" />
                    <ChecklistRow label="Underlying data" checked={include.underlying_data} onChange={v => setInclude(p => ({ ...p, underlying_data: v }))} testid="ai-inc-data" />
                    <ChecklistRow label="Executive summary" checked={include.summary} onChange={v => setInclude(p => ({ ...p, summary: v }))} testid="ai-inc-summary" />
                    <ChecklistRow label="Metric definitions (certified)" checked={include.definitions} onChange={v => setInclude(p => ({ ...p, definitions: v }))} testid="ai-inc-defs" />
                    <ChecklistRow label="Key findings" checked={include.findings} onChange={v => setInclude(p => ({ ...p, findings: v }))} testid="ai-inc-findings" />
                    <ChecklistRow label="Methodology and assumptions" checked={include.methodology} onChange={v => setInclude(p => ({ ...p, methodology: v }))} testid="ai-inc-method" />
                    <ChecklistRow label="Root cause analysis" checked={include.root_causes} onChange={v => setInclude(p => ({ ...p, root_causes: v }))} testid="ai-inc-rca" />
                    <ChecklistRow label="Source citations and lineage" checked={include.lineage} onChange={v => setInclude(p => ({ ...p, lineage: v }))} testid="ai-inc-lineage" />
                    <ChecklistRow label="Recommendations" checked={include.recommendations} onChange={v => setInclude(p => ({ ...p, recommendations: v }))} testid="ai-inc-recs" />
                    <ChecklistRow label="Filter selections" checked={include.filters} onChange={v => setInclude(p => ({ ...p, filters: v }))} testid="ai-inc-filters" />
                    <ChecklistRow label="Visualizations (charts and tables)" checked={include.visuals} onChange={v => setInclude(p => ({ ...p, visuals: v }))} testid="ai-inc-visuals" />
                    <ChecklistRow label="Evaluation date" checked={include.as_of} onChange={v => setInclude(p => ({ ...p, as_of: v }))} testid="ai-inc-asof" />
                    <ChecklistRow label="Include AI² conversation history" checked={include.conversation} onChange={v => setInclude(p => ({ ...p, conversation: v }))} testid="ai-inc-convo" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">Filters</Label>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  {[['current', 'Current selections'], ['default', 'Default filters'], ['none', 'No filters (all data)'], ['choose', 'Choose filters…']].map(([k, l]) => (
                    <button key={k} onClick={() => setFilterMode(k)} data-testid={`export-filter-${k}`}
                      className={`px-2 py-1.5 rounded-lg border text-[11px] text-left transition-colors ${
                        filterMode === k ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border/30 text-muted-foreground hover:border-border/60'}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {activeFilters.length > 0 && (
                  <div className="mt-2 rounded-lg border border-border/40 bg-muted/20 p-2 text-[11px]">
                    <p className="text-muted-foreground/70 uppercase text-[9px] tracking-wide mb-1">Effective selections</p>
                    {activeFilters.map((f, i) => (
                      <div key={i} className="flex justify-between"><span className="text-muted-foreground">{f.label}</span><span className="text-foreground">{f.value}</span></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'data' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Values</Label>
                <div className="flex gap-1.5 mt-1">
                  {[['formatted', 'Formatted'], ['raw', 'Raw']].map(([k, l]) => (
                    <button key={k} onClick={() => setValuesMode(k)} data-testid={`export-values-${k}`}
                      className={`px-3 py-1.5 rounded-lg border text-[11px] transition-colors ${valuesMode === k ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border/30 text-muted-foreground'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Row limit per dataset</Label>
                <Input type="number" min={1} max={50000} value={rowLimit}
                  onChange={e => setRowLimit(Math.max(1, Math.min(50000, parseInt(e.target.value || '1', 10))))}
                  className="mt-1 h-8 w-32 text-xs" data-testid="export-row-limit" />
                <p className="text-[10px] text-muted-foreground/70 mt-1">Rows beyond the limit are truncated and flagged in the file (max 50,000).</p>
              </div>

              {!isAi && primaryVisual && colOrder.length > 0 && (
                <div data-testid="export-column-picker">
                  <Label className="text-xs text-muted-foreground">Columns ({previewCols.length} of {colOrder.length})</Label>
                  <div className="mt-1 space-y-0.5 rounded-lg border border-border/40 p-2 max-h-44 overflow-auto">
                    {colOrder.map((c, i) => (
                      <div key={c} className="flex items-center gap-2 group" data-testid={`export-col-${c}`}>
                        <input type="checkbox" checked={!colHidden[c]} onChange={e => setColHidden(h => ({ ...h, [c]: !e.target.checked }))} className="accent-primary" data-testid={`export-col-check-${c}`} />
                        <span className="text-[11px] text-foreground flex-1 truncate">{(primaryVisual.column_labels || {})[c] || c}</span>
                        <button disabled={i === 0} onClick={() => moveCol(i, -1)} className="opacity-40 group-hover:opacity-100 disabled:opacity-10 text-muted-foreground hover:text-foreground" data-testid={`export-col-up-${c}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                        <button disabled={i === colOrder.length - 1} onClick={() => moveCol(i, 1)} className="opacity-40 group-hover:opacity-100 disabled:opacity-10 text-muted-foreground hover:text-foreground" data-testid={`export-col-down-${c}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">Uncheck to exclude a column, use the arrows to reorder. Applies to the main table.</p>
                </div>
              )}

              {!isAi && primaryVisual && previewCols.length > 0 && (
                <div data-testid="export-preview">
                  <Label className="text-xs text-muted-foreground">Preview</Label>
                  <div className="mt-1 rounded-lg border border-border/40 overflow-auto max-h-40">
                    <table className="w-full text-[10px]">
                      <thead className="bg-muted/40 sticky top-0">
                        <tr>{previewCols.map(c => <th key={c} className="text-left font-semibold px-2 py-1 whitespace-nowrap text-foreground">{(primaryVisual.column_labels || {})[c] || c}</th>)}</tr>
                      </thead>
                      <tbody>
                        {(primaryVisual.rows || []).slice(0, 3).map((r, ri) => (
                          <React.Fragment key={ri}>
                            <tr className="border-t border-border/20">{previewCols.map(c => <td key={c} className="px-2 py-1 whitespace-nowrap truncate max-w-[160px] text-muted-foreground">{String(r[c] ?? '')}</td>)}</tr>
                            {includeData && (primaryVisual.subtotals || []).filter(st => Number(st.afterRowIndex) === ri).map((st, si) => (
                              <tr key={`st-${ri}-${si}`} data-testid="export-preview-subtotal" className="border-t border-border/20 bg-primary/5 font-medium text-foreground">
                                {previewCols.map(c => <td key={c} className="px-2 py-1 whitespace-nowrap truncate max-w-[160px]">{String((st.cells || {})[c] ?? '')}</td>)}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                        {includeData && primaryVisual.totals?.cells && (
                          <tr data-testid="export-preview-total" className="border-t-2 border-primary/40 bg-primary/10 font-semibold text-foreground">
                            {previewCols.map(c => <td key={c} className="px-2 py-1 whitespace-nowrap truncate max-w-[160px]">{String((primaryVisual.totals.cells || {})[c] ?? '')}</td>)}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">Showing the first {Math.min(3, (primaryVisual.rows || []).length)} of {primaryVisual.total_rows ?? (primaryVisual.rows || []).length} rows with your chosen headers.</p>
                </div>
              )}
            </div>
          )}

          {tab === 'appearance' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Branding</Label>
                <div className="flex gap-1.5 mt-1">
                  {[['customer', 'Customer'], ['cloverleaf', getBrandName()]].map(([k, l]) => (
                    <button key={k} onClick={() => setBranding(k)} data-testid={`export-branding-${k}`}
                      className={`px-3 py-1.5 rounded-lg border text-[11px] transition-colors ${branding === k ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border/30 text-muted-foreground'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs text-muted-foreground">Title</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1 h-8 text-xs" data-testid="export-title" /></div>
              <div><Label className="text-xs text-muted-foreground">Subtitle</Label>
                <Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Optional" className="mt-1 h-8 text-xs" data-testid="export-subtitle" /></div>
            </div>
          )}

          {tab === 'options' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-border/40 p-3">
                <div className="pr-3">
                  <p className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Create with AI²</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Compose an Executive brief (summary, findings, recommendations) from this data. Applies to PDF, Word, PowerPoint and HTML.</p>
                </div>
                <Switch checked={composeAi} onCheckedChange={setComposeAi} data-testid="export-compose-ai" />
              </div>
              <div className="space-y-2 text-xs text-muted-foreground">
                <p>Format-specific options apply automatically:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><span className="text-foreground">PDF / Word / PowerPoint / HTML</span> embed a faithful image of each visual, with data tables and a governance appendix.</li>
                  <li><span className="text-foreground">Excel</span> writes one sheet per dataset plus Summary, Definitions and Filter sheets.</li>
                  <li><span className="text-foreground">Package</span> bundles PDF, Excel, HTML, JSON, per-dataset CSVs and visual images with a manifest.</li>
                </ul>
              </div>
            </div>
          )}

          {tab === 'ai' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-border/40 p-3">
                <div className="pr-3">
                  <p className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Create with AI²</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Let AI² write an Executive brief from this analysis. Applies to PDF, Word, PowerPoint and HTML.</p>
                </div>
                <Switch checked={composeAi} onCheckedChange={setComposeAi} data-testid="export-compose-ai" />
              </div>
              <p className="text-[11px] text-muted-foreground">Uses your workspace's configured AI model. Every statement is grounded in the exported data.</p>
            </div>
          )}

          {tab === 'governance' && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground mb-1">Include reproducibility context so a recipient understands what each number means.</p>
              <ChecklistRow label="Certified metric names & definitions" checked={gov.include_definitions} onChange={v => setGov(p => ({ ...p, include_definitions: v }))} testid="gov-defs" />
              <ChecklistRow label="Metric IDs" checked={gov.include_ids} onChange={v => setGov(p => ({ ...p, include_ids: v }))} testid="gov-ids" />
              <ChecklistRow label="Definition versions" checked={gov.include_versions} onChange={v => setGov(p => ({ ...p, include_versions: v }))} testid="gov-versions" />
              <ChecklistRow label="Filter context" checked={gov.include_filter_context} onChange={v => setGov(p => ({ ...p, include_filter_context: v }))} testid="gov-filters" />
              <ChecklistRow label="Source & lineage" checked={gov.include_lineage} onChange={v => setGov(p => ({ ...p, include_lineage: v }))} testid="gov-lineage" />
              <ChecklistRow label="Disclosure text" checked={gov.include_disclosure} onChange={v => setGov(p => ({ ...p, include_disclosure: v }))} testid="gov-disclosure" />
            </div>
          )}

          {tab === 'delivery' && (
            <div className="space-y-3">
              <div className="flex gap-1.5">
                {[['download', 'Download'], ['email', 'Email'], ['schedule', 'Schedule / Publish']].map(([k, l]) => (
                  <button key={k} onClick={() => setDelivery(k)} data-testid={`export-delivery-${k}`}
                    className={`px-3 py-1.5 rounded-lg border text-[11px] transition-colors ${delivery === k ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border/30 text-muted-foreground'}`}>{l}</button>
                ))}
              </div>
              {delivery === 'email' && (
                <div>
                  <Label className="text-xs text-muted-foreground">Send to (comma-separated)</Label>
                  <Input value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="alex@acme.com, team@acme.com" className="mt-1 text-sm" data-testid="export-email-recipients" />
                  <p className="text-[10px] text-muted-foreground/70 mt-1">We'll generate the {format.toUpperCase()} and email it as an attachment using your workspace email settings.</p>
                </div>
              )}
              {delivery === 'schedule' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Recipients (comma-separated)</Label>
                    <Input value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="alex@acme.com, team@acme.com" className="mt-1 text-sm" data-testid="export-recipients" />
                  </div>
                  <SchedulePicker value={spec} onChange={setSpec} />
                  <p className="text-[10px] text-muted-foreground/70">Scheduled runs reuse these exact export settings ({format.toUpperCase()}, {scope.replace(/_/g, ' ')}).</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pixel-perfect toggle — dashboard PDF only */}
        {format === 'pdf' && typeof onNativePdf === 'function' && !nativeReportPdf && !isBusy && (
          <div className="px-6 pb-2 flex items-center justify-between" data-testid="pixel-perfect-row">
            <div>
              <Label className="text-xs text-foreground">Pixel-perfect (match on-screen)</Label>
              <p className="text-[10px] text-muted-foreground">Renders the dashboard exactly as shown. Slower, best for visuals &amp; charts.</p>
            </div>
            <Switch checked={pixelPerfect} onCheckedChange={setPixelPerfect} data-testid="pixel-perfect-toggle" />
          </div>
        )}
        {/* Footer */}
        <div className="px-6 py-4 border-t border-border/40 flex items-center gap-3">
          {isBusy ? (
            <div className="flex-1 flex items-center gap-3" data-testid="export-progress">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-muted-foreground" data-testid="export-progress-label">
                    {(exportProgress && exportProgress.label) || 'Exporting…'}
                  </span>
                  {exportProgress && exportProgress.total > 1 && (
                    <span className="text-[11px] text-muted-foreground" data-testid="export-progress-count">
                      {Math.min(exportProgress.current + 1, exportProgress.total)}/{exportProgress.total}
                    </span>
                  )}
                </div>
                <Progress
                  value={(exportProgress && exportProgress.total > 1) ? Math.round((exportProgress.current / exportProgress.total) * 100) : 15}
                  className={(exportProgress && exportProgress.total > 1) ? '' : 'animate-pulse'}
                  data-testid="export-progress-bar" />
              </div>
              <Button variant="outline" onClick={cancelExport} className="h-8 text-xs" data-testid="export-cancel-running">
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 flex items-center gap-2">
                <Label className="text-[10px] text-muted-foreground whitespace-nowrap">File name</Label>
                <Input value={filename} onChange={e => setFilename(e.target.value)} className="h-8 text-xs" data-testid="export-filename" />
                <span className="text-[10px] text-muted-foreground">.{fmt.ext}</span>
              </div>
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-8 text-xs" data-testid="export-cancel">Cancel</Button>
              <Button onClick={doExport} disabled={isBusy} className="h-8 text-xs bg-primary text-primary-foreground" data-testid="export-submit">
                {delivery === 'schedule' ? <CalendarClock className="w-3.5 h-3.5 mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                {delivery === 'schedule' ? 'Schedule' : delivery === 'email' ? 'Email Export' : `Export ${format.toUpperCase()}`}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
